import { faEdit, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { Modal, Form, FloatingLabel, Row, Stack } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import axiosInstance from 'utils/axiosInterceptors';
import { ColumnDef } from '@tanstack/react-table';
import { toastConfig } from 'utils/toastConfig';
import DataTable from 'pages/apps/crm/BranchProductInventory/DataTable';
import Button from 'components/base/Button';

interface VehicleImageCategory {
  vehicleImageCategoryId: number;
  vehicleImageCategoryName: string;
}



const VehicleImageCategories = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<VehicleImageCategory | null>(null);
  const [formData, setFormData] = useState({ name: '' });

  const fetchData = async (
    page: number,
    pageSize: number,
    search: string,
    orderBy: string,
    orderByDesc: boolean
  ) => {
    const { data } = await axiosInstance.get(`/api/v1/vehicleImageCategories`, {
      params: {
        page,
        pageSize,
        search,
        orderBy,
        orderByDesc
      }
    });
    return data;
  };

  const handleEditCategory = (category: VehicleImageCategory) => {
    setSelectedCategory(category);
    setFormData({ name: category.vehicleImageCategoryName });
    setShowEditModal(true);
  };

  const handleDeleteCategory = (category: VehicleImageCategory) => {
    setSelectedCategory(category);
    setShowDeleteModal(true);
  };

  const columns: ColumnDef<VehicleImageCategory>[] = [
    {
      accessorKey: 'vehicleImageCategoryId',
      header: 'ID',
      enableSorting: false
    },
    {
      accessorKey: 'vehicleImageCategoryName',
      header: 'Category Name',
      enableSorting: true
    },
    {
      header: 'Actions',
      cell: ({ row: { original } }) => (
        <div className="d-flex gap-2">
          <Button
            variant="link"
            size="sm"
            className="p-0"
            onClick={() => handleEditCategory(original)}
          >
            <FontAwesomeIcon icon={faEdit} className="text-primary" />
          </Button>
          <Button
            variant="link"
            size="sm"
            className="p-0"
            onClick={() => handleDeleteCategory(original)}
          >
            <FontAwesomeIcon icon={faTrash} className="text-danger" />
          </Button>
        </div>
      )
    }
  ];

  

  const handleAdd = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a category name', toastConfig);
      return;
    }

    try {
      await axiosInstance.post('/api/v1/vehicleImageCategories', {
        name: formData.name.trim()
      });
      toast.success('Image category created successfully', toastConfig);
      setShowAddModal(false);
      setFormData({ name: '' });
      // Refresh the table data
      window.location.reload();
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || error.response?.data?.errorMessage || 'Failed to create image category';
        toast.error(errorMessage, toastConfig);
      } else {
        toast.error('Failed to create image category', toastConfig);
      }
    }
  };

  const handleEdit = async () => {
    if (!selectedCategory || !formData.name.trim()) {
      toast.error('Please enter a category name', toastConfig);
      return;
    }

    try {
      await axiosInstance.put(`/api/v1/vehicleImageCategories/${selectedCategory.vehicleImageCategoryId}`, {
        name: formData.name.trim()
      });
      toast.success('Image category updated successfully', toastConfig);
      setShowEditModal(false);
      setSelectedCategory(null);
      setFormData({ name: '' });
      // Refresh the table data
      window.location.reload();
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || error.response?.data?.errorMessage || 'Failed to update image category';
        toast.error(errorMessage, toastConfig);
      } else {
        toast.error('Failed to update image category', toastConfig);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;

    try {
      await axiosInstance.delete(`/api/v1/vehicleImageCategories/${selectedCategory.vehicleImageCategoryId}`);
      toast.success('Image category deleted successfully', toastConfig);
      setShowDeleteModal(false);
      setSelectedCategory(null);
      // Refresh the table data
      window.location.reload();
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorMessage = error.response?.data?.message || error.response?.data?.errorMessage || 'Failed to delete image category';
        toast.error(errorMessage, toastConfig);
      } else {
        toast.error('Failed to delete image category', toastConfig);
      }
    }
  };

  return (
    <>
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-baseline mb-2"
      >
        <Row lg="1">
          <h4>Image Categories</h4>
        </Row>
      </Stack>
      <div className="mb-6">
        <DataTable
          queryKey={['vehicleImageCategories']}
          queryFn={fetchData}
          columns={columns}
          rightSideActions={
            <Row lg="auto">
              <Button
                variant="primary"
                className="w-10 mb-3"
                onClick={() => setShowAddModal(true)}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Add Image Category
              </Button>
            </Row>
          }
        />
      </div>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Image Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formGroupCategoryName">
              <FloatingLabel
                controlId="floatingCategoryName"
                label="Category Name"
                className="mb-2"
              >
                <Form.Control
                  value={formData.name}
                  onChange={e => setFormData({ name: e.target.value })}
                  type="text"
                  placeholder="Category Name"
                />
              </FloatingLabel>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAdd}>
            Add Category
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Image Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3" controlId="formGroupCategoryName">
              <FloatingLabel
                controlId="floatingCategoryName"
                label="Category Name"
                className="mb-2"
              >
                <Form.Control
                  value={formData.name}
                  onChange={e => setFormData({ name: e.target.value })}
                  type="text"
                  placeholder="Category Name"
                />
              </FloatingLabel>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEdit}>
            Update Category
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Image Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete the category "{selectedCategory?.vehicleImageCategoryName}"?
            This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VehicleImageCategories; 